import React from 'react';
import { Text } from '@react-three/drei';

function House({ position, size, onHover, component, type = 'grid', onClick }) {
  const [width = 2.8, height = 2, depth = 2.8] = size || [];

  // Scale the model so the provided `size` fits the original model dimensions
  const scale = [width / 2.8, height / 2.8, depth / 2.8];

  return (
    <group
      position={position}
      scale={scale}
      // rotation={[0, -Math.PI /6, 0]}
    >
      {/* Base slab */}
      <mesh position={[0, -1.2, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.8, 0.4, 2.8]} />
        <meshStandardMaterial color='#7a9fb0' metalness={0.3} roughness={0.6} />
      </mesh>

      {/* Main house body (interactive) */}
      <mesh
        position={[0, 0, 0]}
        castShadow
        receiveShadow
        onPointerOver={() => onHover && onHover(component?.name)}
        onPointerOut={() => onHover && onHover(null)}
        onClick={() => onClick && onClick(component)}
      >
        <boxGeometry args={[2.4, 2, 2.4]} />
        <meshStandardMaterial color='#f5e6d3' metalness={0.1} roughness={0.8} />
      </mesh>

      {/* Front roof face */}
      <mesh position={[0, 1.1, 0.6]} castShadow receiveShadow>
        <bufferGeometry>
          <bufferAttribute
            attach='positions'
            count={4}
            array={
              new Float32Array([
                -1.2,
                0,
                -0.6, // bottom left
                1.2,
                0,
                -0.6, // bottom right
                0,
                1.2,
                0, // top peak
                0,
                1.2,
                0, // top peak
              ])
            }
            itemSize={3}
          />
          <bufferAttribute
            attach='index'
            count={6}
            array={new Uint32Array([0, 1, 2, 0, 2, 3])}
            itemSize={1}
          />
        </bufferGeometry>
        <meshStandardMaterial
          color='#d97a5f'
          metalness={0.2}
          roughness={0.7}
          side={2}
        />
      </mesh>

      {/* Back roof face */}
      <mesh position={[0, 1.1, -0.6]} castShadow receiveShadow>
        <bufferGeometry>
          <bufferAttribute
            attach='positions'
            count={4}
            array={
              new Float32Array([
                -1.2,
                0,
                0.6, // bottom left
                1.2,
                0,
                0.6, // bottom right
                0,
                1.2,
                0, // top peak
                0,
                1.2,
                0, // top peak
              ])
            }
            itemSize={3}
          />
          <bufferAttribute
            attach='index'
            count={6}
            array={new Uint32Array([0, 2, 1, 0, 3, 2])}
            itemSize={1}
          />
        </bufferGeometry>
        <meshStandardMaterial
          color='#c75d47'
          metalness={0.2}
          roughness={0.7}
          side={2}
        />
      </mesh>

      {/* Roof ridge */}
      <mesh position={[0, 2.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.5, 0.15, 0.15]} />
        <meshStandardMaterial color='#a04735' metalness={0.3} roughness={0.6} />
      </mesh>

      {/* Roof edge accent - front */}
      <mesh position={[0, 1.05, 1.25]} castShadow receiveShadow>
        <boxGeometry args={[2.6, 0.12, 0.2]} />
        <meshStandardMaterial color='#ffa940' metalness={0.2} roughness={0.5} />
      </mesh>

      {/* Chimney */}
      <mesh position={[-0.6, 1.4, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.3, 1.2, 0.25]} />
        <meshStandardMaterial color='#c75d47' metalness={0.2} roughness={0.7} />
      </mesh>

      {/* Chimney top */}
      <mesh position={[-0.6, 2.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.4, 0.2, 0.35]} />
        <meshStandardMaterial color='#a04735' metalness={0.3} roughness={0.6} />
      </mesh>

      {/* Door */}
      <mesh position={[0, 0.05, 1.21]} castShadow receiveShadow>
        <boxGeometry args={[0.6, 1.2, 0.1]} />
        <meshStandardMaterial
          color='#b8e0d2'
          metalness={0.15}
          roughness={0.75}
        />
      </mesh>

      {/* Door handle */}
      <mesh position={[0.25, 0.05, 1.25]} castShadow receiveShadow>
        <cylinderGeometry
          args={[0.08, 0.08, 0.05, 16]}
          rotation={[Math.PI / 2, 0, 0]}
        />
        <meshStandardMaterial color='#c4a57b' metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Windows and frames - grouped for clarity */}
      {[
        [-0.7, 0.6, 1.23],
        [0.7, 0.6, 1.23],
        [-1.23, 0.6, -0.2],
        [0, 1.3, 1.23],
      ].map((pos, i) => (
        <mesh key={i} position={pos} castShadow receiveShadow>
          <boxGeometry
            args={i === 2 ? [0.05, 0.52, 0.52] : [0.52, 0.52, 0.05]}
          />
          <meshStandardMaterial
            color='#8b7355'
            metalness={0.3}
            roughness={0.6}
          />
        </mesh>
      ))}

      {/* Decorative top label */}
      <group position={[4, height - 2, 0]}>
        {/* First line - BIG FONT */}
        <Text
          fontSize={0.55}
          color='black'
          anchorX='center'
          anchorY='middle'
          outlineColor='#000000ff'
          outlineWidth={0.01}
          fontWeight={700}
          position={[0, 0, 0]}
        >
          {component.name}
        </Text>

        {/* Next 2 lines - SMALL FONT only if value exists */}
        {(component.details?.Brand_name ||
          component.details?.Quantity ||
          component.details?.Specification) && (
          <Text
            fontSize={0.9}
            color='black'
            anchorX='center'
            anchorY='top'
            position={[0, -1.4, 0]}
          >
            {`${component.details?.Brand_name || ''}\nx${component.details?.Quantity || '0'}\n${component.details?.Specification || ''}`}
          </Text>
        )}
      </group>
    </group>
  );
}

export default House;
